import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { resolveAnalyticsRoute } from '../analytics/eventTypes'
import {
  beginAnalyticsInteraction,
  createPageViewId,
  getAnalyticsPageContext,
  setPreviousPage,
  trackAnalytics,
} from '../utils/analytics'

interface PageState {
  path: string
  pageViewId: string
  startTime: number
  activeStartTime: number
  activeDurationMs: number
  maxScrollPercent: number
  reachedBottom: boolean
  scrollEvents: number
  firstScrollMs?: number
  lastScrollMs?: number
  clickCount: number
  milestones: Set<string>
  bottomSegments: Set<string>
  exited: boolean
}

interface PendingExit {
  path: string
  state: PageState
  timer: number
  report: () => void
}

function getScrollPercent() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop
  const scrollHeight = document.documentElement.scrollHeight
  const clientHeight = document.documentElement.clientHeight
  const scrollable = Math.max(scrollHeight - clientHeight, 1)
  return Math.min(100, Math.max(0, (scrollTop / scrollable) * 100))
}

function getVisibleEventSnapshot() {
  const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-visible-event-id]'))
  const seen = new Set<string>()
  const visible: string[] = []

  for (const element of elements) {
    const rect = element.getBoundingClientRect()
    const eventId = element.dataset.visibleEventId
    if (!eventId || seen.has(eventId) || rect.bottom <= 0 || rect.top >= window.innerHeight) continue
    seen.add(eventId)
    visible.push(eventId)
  }

  return {
    scroll_y: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
    visible_event_ids: visible.slice(0, 8),
    top_visible_event_id: visible[0],
    bottom_visible_event_id: visible[visible.length - 1],
  }
}

function getScrollSegment(context: Record<string, unknown>) {
  const value = context.scroll_segment ?? context.current_page ?? context.loaded_count ?? 'default'
  return String(value || 'default')
}

function getScrollScope(context: Record<string, unknown>) {
  const value = context.scroll_scope ?? getScrollSegment(context)
  return String(value || 'default')
}

export function PageAnalyticsTracker() {
  const location = useLocation()
  const pendingExitRef = useRef<PendingExit | null>(null)

  useEffect(() => {
    const resolved = resolveAnalyticsRoute(location.pathname)
    const path = `${location.pathname}${location.search}`
    const pending = pendingExitRef.current
    let state: PageState
    let isResumedStrictModeEffect = false

    if (pending) {
      window.clearTimeout(pending.timer)
      pendingExitRef.current = null
      if (pending.path === path && !pending.state.exited) {
        state = pending.state
        isResumedStrictModeEffect = true
      } else {
        pending.report()
        state = createState(path)
      }
    } else {
      state = createState(path)
    }

    if (!isResumedStrictModeEffect) {
      void trackAnalytics({
        event_type: 'page_enter',
        page: resolved.page,
        path,
        route: resolved.route,
        page_view_id: state.pageViewId,
        data: { search: location.search, route_params: resolved.routeParams },
      })
    }

    const reportExit = (reason: string, useBeacon = false) => {
      if (state.exited) return
      state.exited = true
      const now = Date.now()
      const activeDurationMs = state.activeDurationMs +
        (document.visibilityState === 'visible' ? now - state.activeStartTime : 0)
      const durationMs = now - state.startTime
      const maxScrollPercent = Math.round(Math.max(state.maxScrollPercent, getScrollPercent()))
      const reachedBottom = state.reachedBottom || maxScrollPercent >= 95

      void trackAnalytics({
        event_type: 'page_exit',
        page: resolved.page,
        path,
        route: resolved.route,
        page_view_id: state.pageViewId,
        data: {
          ...getAnalyticsPageContext(),
          ...getVisibleEventSnapshot(),
          reason,
          duration_ms: durationMs,
          active_duration_ms: activeDurationMs,
          idle_duration_ms: Math.max(0, durationMs - activeDurationMs),
          max_scroll_percent: maxScrollPercent,
          reached_bottom: reachedBottom,
          read_ratio: maxScrollPercent / 100,
          scroll_events: state.scrollEvents,
          first_scroll_ms: state.firstScrollMs,
          last_scroll_ms: state.lastScrollMs,
          click_count: state.clickCount,
          content_height: document.documentElement.scrollHeight,
          viewport_height: window.innerHeight,
        },
      }, useBeacon)
    }

    const handleScroll = () => {
      const now = Date.now()
      const percent = getScrollPercent()
      const pageContext = getAnalyticsPageContext()
      const scrollSegment = getScrollSegment(pageContext)
      const scrollScope = getScrollScope(pageContext)
      state.scrollEvents += 1
      state.maxScrollPercent = Math.max(state.maxScrollPercent, percent)
      state.lastScrollMs = now - state.startTime
      if (state.firstScrollMs === undefined) state.firstScrollMs = now - state.startTime

      for (const milestone of [25, 50, 75, 95]) {
        const milestoneKey = `${scrollScope}:${milestone}`
        if (percent < milestone || state.milestones.has(milestoneKey)) continue
        state.milestones.add(milestoneKey)
        void trackAnalytics({
          event_type: 'scroll_milestone',
          page: resolved.page,
          path,
          route: resolved.route,
          page_view_id: state.pageViewId,
          data: {
            ...pageContext,
            ...getVisibleEventSnapshot(),
            scroll_scope: scrollScope,
            scroll_segment: scrollSegment,
            scroll_percent: milestone,
            time_to_reach_ms: now - state.startTime,
            content_height: document.documentElement.scrollHeight,
            viewport_height: window.innerHeight,
          },
        })
      }

      if (percent >= 95 && !state.bottomSegments.has(scrollScope)) {
        state.bottomSegments.add(scrollScope)
        state.reachedBottom = true
        void trackAnalytics({
          event_type: 'scroll_bottom',
          page: resolved.page,
          path,
          route: resolved.route,
          page_view_id: state.pageViewId,
          data: {
            ...pageContext,
            ...getVisibleEventSnapshot(),
            scroll_scope: scrollScope,
            scroll_segment: scrollSegment,
            time_to_reach_ms: now - state.startTime,
          },
        })
      }
    }

    const handleClick = (event: MouseEvent) => {
      state.clickCount += 1
      const target = event.target as HTMLElement | null
      const element = target?.closest('[data-analytics-id],button,a,[role="button"]') as HTMLElement | null
      if (!element) return

      const interactionId = beginAnalyticsInteraction()
      const buttonText = (element.getAttribute('aria-label') || element.textContent || '').trim().slice(0, 80)
      const href = element instanceof HTMLAnchorElement ? element.href : undefined
      const eventElement = element.closest('[data-event-id]') as HTMLElement | null
      const analyticsId = element.dataset.analyticsId ||
        element.getAttribute('aria-label') ||
        buttonText.toLowerCase().replace(/\s+/g, '-').slice(0, 60) ||
        element.tagName.toLowerCase()
      void trackAnalytics({
        event_type: 'button_click',
        page: resolved.page,
        path,
        route: resolved.route,
        page_view_id: state.pageViewId,
        interaction_id: interactionId,
        data: {
          analytics_id: analyticsId,
          button_text: buttonText,
          tag_name: element.tagName.toLowerCase(),
          href,
          target_event_id: element.dataset.eventId || eventElement?.dataset.eventId,
          target_narrative: element.dataset.narrative || eventElement?.dataset.narrative,
          button_role: element.dataset.analyticsRole,
        },
      })
    }

    const handleVisibilityChange = () => {
      const now = Date.now()
      if (document.visibilityState === 'hidden') {
        state.activeDurationMs += now - state.activeStartTime
        const durationMs = now - state.startTime
        const maxScrollPercent = Math.round(Math.max(state.maxScrollPercent, getScrollPercent()))
        void trackAnalytics({
          event_type: 'page_hidden', page: resolved.page, path, route: resolved.route,
          page_view_id: state.pageViewId,
          data: {
            ...getAnalyticsPageContext(),
            ...getVisibleEventSnapshot(),
            duration_ms: durationMs,
            active_duration_ms: state.activeDurationMs,
            idle_duration_ms: Math.max(0, durationMs - state.activeDurationMs),
            max_scroll_percent: maxScrollPercent,
            reached_bottom: state.reachedBottom || maxScrollPercent >= 95,
            read_ratio: maxScrollPercent / 100,
            scroll_events: state.scrollEvents,
            first_scroll_ms: state.firstScrollMs,
            last_scroll_ms: state.lastScrollMs,
            click_count: state.clickCount,
            content_height: document.documentElement.scrollHeight,
            viewport_height: window.innerHeight,
          },
        }, true)
      } else {
        state.activeStartTime = now
        void trackAnalytics({
          event_type: 'page_visible', page: resolved.page, path, route: resolved.route,
          page_view_id: state.pageViewId,
        })
      }
    }

    const handleBeforeUnload = () => reportExit('beforeunload', true)
    const handlePageHide = () => reportExit('pagehide', true)
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('click', handleClick, true)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
      setPreviousPage(resolved.page)

      const report = () => reportExit('route_change', true)
      const timer = window.setTimeout(() => {
        report()
        pendingExitRef.current = null
      }, 0)
      pendingExitRef.current = { path, state, timer, report }
    }
  }, [location.pathname, location.search])

  return null
}

function createState(path: string): PageState {
  return {
    path,
    pageViewId: createPageViewId(),
    startTime: Date.now(),
    activeStartTime: Date.now(),
    activeDurationMs: 0,
    maxScrollPercent: getScrollPercent(),
    reachedBottom: false,
    scrollEvents: 0,
    clickCount: 0,
    milestones: new Set<string>(),
    bottomSegments: new Set<string>(),
    exited: false,
  }
}
